import { Box, type SxProps, styled, type Theme } from "@mui/material";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { CompletenessReport, CompletenessShift } from "../../../downloads/aiPrintout/completeness";
import { buildHarnessSrcdoc, type HarnessFrameSecrets } from "../../../downloads/aiPrintout/harness";
import {
	acceptHarnessMessage,
	HARNESS_READY_TIMEOUT_MS,
	type HarnessFailureKind,
	type HarnessPrintMessage,
	type HarnessRenderMessage,
	type HarnessToHostMessage,
} from "../../../downloads/aiPrintout/harnessProtocol";
import { NEUTRAL } from "../../../theme/tokens";

/** What the latest run produced. `rendering` is reported each time a new run starts. */
export type SandboxRenderResult =
	| { status: "rendering" }
	| { status: "rendered"; report: CompletenessReport; renderMs: number; heightPx: number; widthPx: number }
	| { status: "failed"; kind: HarnessFailureKind; message: string };

export type SandboxedHtmlRendererHandle = {
	/**
	 * Opens the browser's print dialog for the rendered page only (its own `@page` applies). Ignored unless the last
	 * render passed. `title` is the page's title while it prints, which browsers suggest as the PDF's file name.
	 */
	print: (title: string) => void;
};

export type SandboxedHtmlRendererProps = {
	/** A JavaScript module body defining `function render(data) { return "<html>"; }`. Untrusted. */
	source: string;
	/** Plain, structured-cloneable JSON handed to `render`. */
	data: unknown;
	/** Items that must each be visibly on the page, tagged `data-shift-id`, with their text. Their absence fails the report. */
	required: CompletenessShift[];
	/** Text direction of the rendered page. */
	dir: "ltr" | "rtl";
	onReport: (result: SandboxRenderResult) => void;
	/** Already-translated accessible name for the frame. */
	title: string;
	dataTestId: string;
	sx?: SxProps<Theme>;
};

// The page is paper, so the frame is white in both themes.
const Frame = styled("iframe")(({ theme }) => ({
	width: "100%",
	height: "100%",
	border: "none",
	display: "block",
	backgroundColor: theme.palette.common.white,
}));

const createFrameSecrets = (): HarnessFrameSecrets => ({ nonce: crypto.randomUUID(), token: crypto.randomUUID() });

const NAVIGATED_MESSAGE = "The frame navigated away from the printout, so it was reloaded";

/**
 * The app tab's title while the frame prints. Chrome names a PDF saved from the print dialog after the tab, not the
 * frame being printed, so the tab takes the print's title until the frame says printing is over (or this unmounts).
 */
const usePrintTitle = () => {
	/** The tab's own title while a print's is shown; `null` when nothing is printing. */
	const tabTitleRef = useRef<string | null>(null);
	const restore = useCallback(() => {
		if (tabTitleRef.current === null) return;
		document.title = tabTitleRef.current;
		tabTitleRef.current = null;
	}, []);
	const apply = useCallback((title: string) => {
		if (tabTitleRef.current === null) tabTitleRef.current = document.title;
		document.title = title;
	}, []);
	useEffect(() => restore, [restore]);
	return useMemo(() => ({ apply, restore }), [apply, restore]);
};

const resultOf = (message: Exclude<HarnessToHostMessage, { type: "ready" } | { type: "leaving" } | { type: "print-ended" }>): SandboxRenderResult =>
	message.type === "rendered"
		? { status: "rendered", report: message.report, renderMs: message.renderMs, heightPx: message.heightPx, widthPx: message.widthPx }
		: { status: "failed", kind: message.kind, message: message.message };

/**
 * Runs untrusted template code and shows its HTML, caged twice: the code runs in a Web Worker that is
 * killed after a time limit, and the page sits in an opaque-origin iframe (`allow-scripts allow-modals`
 * only — no same-origin, pop-ups, forms or top-level navigation) whose CSP blocks every network request.
 * After each render the harness checks that every `required` item is visibly on the page and reports.
 *
 * The frame can still navigate itself (a followed link, a dropped file), and the page it lands on keeps the
 * frame's window and "null" origin. So the harness strips link targets and cancels link clicks, every message
 * must carry this frame's secret token, and any `load` after the first — or the harness saying it is leaving —
 * means the frame left the harness: nothing more is posted to it, the run reports `navigated`, and a fresh
 * frame (new nonce and token) replaces it. No `ready` within `HARNESS_READY_TIMEOUT_MS` reports `not-ready`.
 *
 * Messages are accepted only from this frame's window, from origin "null", with its token, in the protocol's
 * shape, and for the current run. Sizes to its parent — callers pass `flex: 1` / a height via `sx`.
 */
export const SandboxedHtmlRenderer = forwardRef<SandboxedHtmlRendererHandle, SandboxedHtmlRendererProps>(
	({ source, data, required, dir, onReport, title, dataTestId, sx }, ref) => {
		const frameRef = useRef<HTMLIFrameElement>(null);
		const [frameSecrets, setFrameSecrets] = useState<HarnessFrameSecrets>(createFrameSecrets);
		const tokenRef = useRef<string>(frameSecrets.token);
		const loadCountRef = useRef<number>(0);
		const srcDoc = useMemo(() => buildHarnessSrcdoc(frameSecrets), [frameSecrets]);
		const [isReady, setIsReady] = useState<boolean>(false);
		const runIdRef = useRef<number>(0);
		const onReportRef = useRef(onReport);
		onReportRef.current = onReport;
		const printTitle = usePrintTitle();

		const replaceFrame = useCallback(() => {
			const secrets = createFrameSecrets();
			// From here on only the new frame's messages count; the run in flight can never report.
			tokenRef.current = secrets.token;
			runIdRef.current += 1;
			loadCountRef.current = 0;
			setIsReady(false);
			setFrameSecrets(secrets);
			onReportRef.current({ status: "failed", kind: "navigated", message: NAVIGATED_MESSAGE });
		}, []);

		const handleLoad = useCallback(
			(token: string) => {
				if (token !== tokenRef.current) return;
				loadCountRef.current += 1;
				// The first load is the harness page itself; any later one is the frame having left it.
				if (loadCountRef.current > 1) replaceFrame();
			},
			[replaceFrame],
		);

		useEffect(() => {
			const handleMessage = (event: MessageEvent<unknown>) => {
				const frameWindow = frameRef.current === null ? null : frameRef.current.contentWindow;
				const message = acceptHarnessMessage(event, frameWindow, runIdRef.current, tokenRef.current);
				if (message === null) return;
				if (message.type === "ready") setIsReady(true);
				else if (message.type === "print-ended") printTitle.restore();
				else if (message.type === "leaving") replaceFrame();
				else onReportRef.current(resultOf(message));
			};
			window.addEventListener("message", handleMessage);
			return () => window.removeEventListener("message", handleMessage);
		}, [replaceFrame, printTitle]);

		useEffect(() => {
			if (isReady) return;
			const timer = setTimeout(
				() => onReportRef.current({ status: "failed", kind: "not-ready", message: `The printout sandbox did not start within ${HARNESS_READY_TIMEOUT_MS} ms` }),
				HARNESS_READY_TIMEOUT_MS,
			);
			return () => clearTimeout(timer);
		}, [isReady]);

		useEffect(() => {
			const frameWindow = frameRef.current === null ? null : frameRef.current.contentWindow;
			if (!isReady || frameWindow === null) return;
			runIdRef.current += 1;
			onReportRef.current({ status: "rendering" });
			// "*" is the only target an opaque-origin frame accepts; what is posted is data the viewer already sees.
			frameWindow.postMessage({ type: "render", runId: runIdRef.current, source, data, required, dir } satisfies HarnessRenderMessage, "*");
		}, [isReady, source, data, required, dir]);

		useImperativeHandle(
			ref,
			() => ({
				print: (title: string) => {
					const frameWindow = frameRef.current === null ? null : frameRef.current.contentWindow;
					if (frameWindow === null) throw new Error("SandboxedHtmlRenderer: print() called before the frame mounted");
					printTitle.apply(title);
					frameWindow.postMessage({ type: "print", title } satisfies HarnessPrintMessage, "*");
				},
			}),
			[printTitle],
		);

		return (
			<Box
				data-testid={dataTestId}
				sx={[{ border: `1px solid ${NEUTRAL[300]}`, borderRadius: 1, overflow: "hidden", minWidth: 0, minHeight: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}
			>
				<Frame
					key={frameSecrets.token}
					ref={frameRef}
					data-testid={`${dataTestId}-frame`}
					title={title}
					sandbox="allow-scripts allow-modals"
					srcDoc={srcDoc}
					onLoad={() => handleLoad(frameSecrets.token)}
				/>
			</Box>
		);
	},
);
SandboxedHtmlRenderer.displayName = "SandboxedHtmlRenderer";
